import {Form, FormField, FormSubmitHandler, Separator} from 'platform/components';
import {Box, Heading, HStack, Space, VStack} from 'platform/foundation';
import {DeepPartial} from 'utility-types';

import {isNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  GetSupplierDiscountGroupResponse,
  PatchSupplierDiscountGroupRequest,
  useGetSuppliersQuery,
  usePatchSupplierDiscountGroupMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {
  getDecimalFromPercentage,
  getOptionsFromAuthorizationProfiles,
  getOptionsFromSuppliers,
  getPercentageFromDecimal,
  handleApiError,
  usePermissions,
} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {DISCOUNT_TYPE_OPTIONS} from '../constants/discountTypeOptions';
import {
  SupplierDiscountGroupForm,
  SupplierDiscountGroupFormSchema,
} from '../types/SupplierDiscountGroupForm';
import {DiscountLevels} from './DiscountLevels';

interface SupplierDiscountGroupDetailFormProps extends RequiredTestIdProps {
  id: string;
  supplierDiscountGroup: GetSupplierDiscountGroupResponse | Nullish;
}

export function SupplierDiscountGroupDetailForm(props: SupplierDiscountGroupDetailFormProps) {
  const navigate = useNavigate();

  const tenantQuery = useGetTenantQuery();

  const suppliersQuery = useGetSuppliersQuery();

  const authorizationProfilesQuery = useGetAuthorizationProfilesQuery(
    {'x-tenant': tenantQuery.data?.id as string},
    {skip: isNil(tenantQuery.data)}
  );

  const [patchSupplierDiscountGroup, {isLoading: isSavingChanges}] =
    usePatchSupplierDiscountGroupMutation();

  const [warehouseDictionaryEdit] = usePermissions({
    permissionKeys: ['warehouseDictionaryEdit'],
  });

  const defaultValues: DeepPartial<SupplierDiscountGroupForm> = {
    supplierId: props.supplierDiscountGroup?.supplierId,
    authorizationProfileId: props.supplierDiscountGroup?.authorizationProfileId,
    supplierDiscountGroupCode: props.supplierDiscountGroup?.supplierDiscountGroupCode,
    discountType: props.supplierDiscountGroup?.typeOfCalculation,
    discountLevels: props.supplierDiscountGroup?.discountLevels.map((discountLevel) => ({
      ...discountLevel,
      discountPercentage: getPercentageFromDecimal(discountLevel.discountPercentage) ?? 0,
    })),
  };

  const handleSubmit: FormSubmitHandler<SupplierDiscountGroupForm> = async (formValues) => {
    const requestBody: PatchSupplierDiscountGroupRequest = {
      typeOfCalculation: formValues.discountType,
      discountLevels: formValues.discountLevels.map((discountLevel) => ({
        supplierDiscountGroupId: discountLevel.supplierDiscountGroupId,
        discountPercentage: getDecimalFromPercentage(discountLevel.discountPercentage) ?? 0,
      })),
    };

    await patchSupplierDiscountGroup(requestBody)
      .unwrap()
      .then(handleNavigateBack)
      .catch(handleApiError);
  };

  const handleNavigateBack = () => {
    navigate(settingsRoutes.warehousesSupplierDiscountGroups);
  };

  return (
    <Form<SupplierDiscountGroupForm>
      experimentalZodSchema={SupplierDiscountGroupFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isDisabled={!warehouseDictionaryEdit}
    >
      {(control) => (
        <VStack spacing={4}>
          <HStack spacing={4} width={200}>
            <Box width="100%">
              <FormField
                isDisabled
                isLoading={suppliersQuery.isLoading}
                control={control}
                name="supplierId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.supplier')}
                options={getOptionsFromSuppliers(suppliersQuery.data)}
                data-testid={suffixTestId('inputs.supplierId', props)}
              />
            </Box>
            <Box width="100%">
              <FormField
                isDisabled
                isLoading={authorizationProfilesQuery.isLoading}
                control={control}
                name="authorizationProfileId"
                type="choice"
                label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                options={getOptionsFromAuthorizationProfiles(authorizationProfilesQuery.data)}
                data-testid={suffixTestId('inputs.authorizationProfileId', props)}
              />
            </Box>
          </HStack>
          <HStack spacing={4} width={200}>
            <Box width="100%">
              <FormField
                isDisabled
                control={control}
                name="supplierDiscountGroupCode"
                type="text"
                label={i18n.t('entity.warehouse.labels.supplierDiscountGroupCode')}
                data-testid={suffixTestId('inputs.supplierDiscountGroupCode', props)}
              />
            </Box>
            <Box width="100%">
              <FormField
                control={control}
                name="discountType"
                type="choice"
                label={i18n.t('entity.warehouse.labels.discountType')}
                options={DISCOUNT_TYPE_OPTIONS}
                data-testid={suffixTestId('inputs.discountType', props)}
              />
            </Box>
          </HStack>

          <Separator spacing={0} />

          <Heading size={4}>{i18n.t('entity.warehouse.labels.discountLevels')}</Heading>
          <DiscountLevels control={control} data-testid={suffixTestId('discountLevels', props)} />

          <Space fillAvailable />

          <SettingsFooter
            actions={[
              {
                isLoading: isSavingChanges,
                type: 'button',
                variant: 'secondary',
                title: i18n.t('general.actions.discardChanges'),
                onClick: handleNavigateBack,
                'data-testid': suffixTestId('actions.discard', props),
              },
              {
                isLoading: isSavingChanges,
                type: 'form-button',
                control,
                buttonType: 'submit',
                variant: 'primary',
                title: i18n.t('general.actions.saveChanges'),
                'data-testid': suffixTestId('actions.submit', props),
              },
            ]}
          />
        </VStack>
      )}
    </Form>
  );
}
